import React, {useCallback, useState} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import useStableValue from 'ui-library/hooks/useStableValue';
import Menu from 'ui-library/menus/Menu';
import OptionContainer from 'ui-library/menus/OptionContainer';

import {GetTableDataFn} from './extractTableDataForSuggestions';
import SuggestedPlotsHoverOption from './SuggestedPlotsHoverOption';
import {prefetchSuggestPlots} from './suggestPlotsState';

const BUTTON_TITLE = 'Add to analysis';

interface SuggestedPlotsDropdownProps {
  entryId: string;
  tableName: string;
  getTableData: GetTableDataFn;
  disabled?: boolean;
  className?: string;
}

export default function SuggestedPlotsDropdown({
  entryId,
  tableName,
  getTableData,
  disabled,
  className,
}: SuggestedPlotsDropdownProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const getTableDataFn = useStableValue<GetTableDataFn>(getTableData);
  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Kick off the suggestions fetch as soon as the dropdown opens so results are more
  // likely to be ready before the user hovers the "AI Suggested Plots" menu item.
  const handleToggle = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (open) {
        prefetchSuggestPlots({entryId, tableName, getTableData: getTableDataFn});
      }
    },
    [entryId, tableName, getTableDataFn]
  );

  return (
    <Tooltip title={BUTTON_TITLE}>
      <Dropdown
        isOpen={isOpen}
        onToggle={handleToggle}
        closeOnInsideClick={false}
        menu={
          <Menu dropLeft={true} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM}>
            <OptionContainer shouldEllipsize={true}>
              <SuggestedPlotsHoverOption
                entryId={entryId}
                tableName={tableName}
                getTableData={getTableData}
                onSuggestionChosen={handleClose}
              />
            </OptionContainer>
          </Menu>
        }
      >
        <Button
          disabled={disabled}
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MINI}
          className={className}
          icon={ModelTypeConstants.ANALYSIS}
          iconAltText={BUTTON_TITLE}
          isDropdown={true}
          dataTestAttrs={{
            component: 'SuggestedPlotsButton',
            element: 'button',
          }}
        />
      </Dropdown>
    </Tooltip>
  );
}
